import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Color, Mesh, Material } from 'three';
import useStore from '../../store/useStore';
import { createRingPatternMaterial, setPatternDark, setPatternGlowColor } from '../../lib/materials/createRingPatternMaterial';
import type { GrooveOptions, WaveOptions } from '../../lib/materials/createRingPatternMaterial';
import type CustomShaderMaterial from 'three-custom-shader-material/vanilla';
import { buildRingFromDxf, innerRadiusFromSize, SCENE_SCALE, type RingMeasurements } from '../../lib/three/ringExtrusion';
import GemsMesh from './GemsMesh';

/**
 * Renders a ring extruded from the DXF profile of the currently selected ring template.
 *
 * - Reads the active template from the store, derives width from `fixedWidth` on the
 *   first ring partial (falls back to store `ringWidth`).
 * - Re-extrudes whenever the selected template or measurements change.
 * - Ring body uses plain PBR metal — only the flush gem pockets (separate mesh) are emissive.
 */
const ExtrudedRingMesh: React.FC = () => {
    const ringTemplates = useStore((s) => s.ringTemplates);
    const selectedTemplateId = useStore((s) => s.selectedTemplateId);
    const ringSize = useStore((s) => s.ringSize);
    const ringWidth = useStore((s) => s.ringWidth);
    const ringHeight = useStore((s) => s.ringHeight);
    const styleMode = useStore((s) => s.styleMode);

    const [ringMesh, setRingMesh] = useState<Mesh | null>(null);
    const [error, setError] = useState<string | null>(null);
    const mountedRef = useRef(true);
    /** Holds the current ring pattern material so the dark-mode subscription can update it. */
    const patternMatRef = useRef<CustomShaderMaterial | null>(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Keep pattern material uniforms in sync with dark mode and glow colour.
    useEffect(() => {
        return useStore.subscribe(
            (s) => ({ isDark: s.isDark, glowColor: s.glowColor }),
            ({ isDark, glowColor }) => {
                if (patternMatRef.current) {
                    setPatternDark(patternMatRef.current, isDark);
                    setPatternGlowColor(patternMatRef.current, glowColor.threeHex);
                }
            },
            { equalityFn: (a, b) => a.isDark === b.isDark && a.glowColor.id === b.glowColor.id },
        );
    }, []);

    // Pick the active template: selected one, or first with DXF files
    const activeTemplate = useMemo(() =>
        (selectedTemplateId
            ? ringTemplates.find((t) => t.id === selectedTemplateId)
            : undefined) ??
        ringTemplates.find(
            (t) => (t.ringPartials[0]?.ringProfile?.dxfFiles?.length ?? 0) > 0,
        ),
    [selectedTemplateId, ringTemplates]);

    const dxfFile = activeTemplate?.ringPartials[0]?.ringProfile?.dxfFiles?.[0];

    // Derive width from the template's ring partial fixedWidth if available
    const effectiveWidth = activeTemplate?.ringPartials[0]?.fixedWidth ?? ringWidth;

    // Derive alloy color from the first alloy of the first ring partial.
    // Matches hd_conf's useStandardMetalMaterials: hex → Color → multiplyScalar(1.3).
    // Falls back to a neutral warm silver if no alloy data is present.
    const alloyColor = useMemo(() => {
        const hexStr = activeTemplate?.ringPartials[0]?.alloys[0]?.color?.hexColorValue;
        if (hexStr) {
            return new Color().setHex(parseInt(hexStr, 16)).multiplyScalar(1.3);
        }
        return new Color('#c8bfb0');
    }, [activeTemplate]);

    // Derive surface parameters from the first surface of the first ring partial.
    //
    // NOTE: averageRoughnessValue in the DB (default 0.75) is the triplanar shader blend
    // parameter used by hd_conf — NOT a bare PBR roughness scalar. It is only meaningful
    // as a roughness value when a roughnessMap texture provides the fine detail on top.
    // Without a roughness map, always use the polished constant (0.05).
    const POLISHED_ROUGHNESS = 0.05;
    const surfaceParams = useMemo(() => {
        const surface = activeTemplate?.ringPartials[0]?.surfaces[0];
        const roughnessMapUrl = surface?.roughnessFile
            ? `/uploads/${surface.roughnessFile.path}` : undefined;
        const normalMapUrl = surface?.normalFile
            ? `/uploads/${surface.normalFile.path}` : undefined;
        // Only trust averageRoughnessValue when a roughness map is present to give it meaning.
        const roughness = roughnessMapUrl
            ? (surface?.averageRoughnessValue ?? POLISHED_ROUGHNESS)
            : POLISHED_ROUGHNESS;
        return { roughness, roughnessMapUrl, normalMapUrl };
    }, [activeTemplate]);

    useEffect(() => {
        if (!dxfFile) {
            setRingMesh(null);
            return;
        }

        const dxfUrl = `/uploads/${dxfFile.file.path}`;
        const measurements: RingMeasurements = {
            size: ringSize,
            width: effectiveWidth,
            height: ringHeight,
        };

        // Pattern material: polished metal PBR with a shader-driven pattern on the surface.
        // All pattern logic lives in the fragment shader — no CSG, no UV needed.
        const coreRadius = (innerRadiusFromSize(ringSize) + ringHeight / 2) * SCENE_SCALE;
        const grooveZ    = +(effectiveWidth / 2) * SCENE_SCALE;
        const grooveTube = Math.max(ringHeight * 0.06, 0.12) * SCENE_SCALE;
        const waveAmp    = (effectiveWidth * 0.18) * SCENE_SCALE;
        const waveBand   = Math.max(ringHeight * 0.06, 0.10) * SCENE_SCALE;

        const groove: GrooveOptions | undefined = styleMode === 'groove'
            ? { coreRadius, z: grooveZ, tube: grooveTube }
            : undefined;
        const wave: WaveOptions | undefined = styleMode === 'wave'
            ? { amplitude: waveAmp, frequency: 8, band: waveBand }
            : undefined;

        const { isDark, glowColor } = useStore.getState();
        const mat = createRingPatternMaterial({
            color: alloyColor,
            metalness: 1.0,
            ...surfaceParams,
            groove,
            wave,
        });
        setPatternDark(mat, isDark);
        setPatternGlowColor(mat, glowColor.threeHex);
        patternMatRef.current = mat;

        let cancelled = false;

        buildRingFromDxf(dxfUrl, measurements, mat)
            .then((mesh) => {
                if (cancelled || !mountedRef.current) {
                    mesh.geometry.dispose();
                    (mesh.material as Material).dispose();
                    return;
                }
                setRingMesh(mesh);
                setError(null);
            })
            .catch((err: unknown) => {
                if (!cancelled && mountedRef.current) {
                    setError(err instanceof Error ? err.message : String(err));
                    setRingMesh(null);
                }
            });

        return () => {
            cancelled = true;
        };
        // Keyed on the stable dxfFile.file.path; depending on the dxfFile object
        // identity would rebuild the mesh on every unrelated store change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dxfFile?.file.path, ringSize, effectiveWidth, ringHeight, alloyColor, surfaceParams, styleMode]);

    // Dispose old geometry when ring mesh changes
    useEffect(() => {
        return () => {
            if (ringMesh) {
                ringMesh.geometry.dispose();
            }
        };
    }, [ringMesh]);

    if (error) {
        console.error('[ExtrudedRingMesh]', error);
    }

    if (!ringMesh) return null;

    // Show gems if the active ring partial has setting types with allowed gems.
    // Note: ring_partial.gems is empty for all rings — gems come from settingType.allowedGems.
    const settingTypes = activeTemplate?.ringPartials[0]?.settingTypes ?? [];
    const hasGems = settingTypes.some((s) => (s.allowedGems?.length ?? 0) > 0);

    return (
        <>
            <primitive
                object={ringMesh}
                castShadow
                receiveShadow
            />
            {hasGems && activeTemplate && styleMode === 'gems' && (
                <GemsMesh
                    ringMesh={ringMesh}
                    activeTemplate={activeTemplate}
                    ringSize={ringSize}
                    ringHeight={ringHeight}
                    patternMat={patternMatRef.current}
                />
            )}
        </>
    );
};

export default ExtrudedRingMesh;
